export type FeatureFlagKey =
  | 'contactForm'
  | 'caseStudies'
  | 'availabilityBanner';

type FeatureFlagDefinition = {
  key: FeatureFlagKey;
  envName: string;
  defaultEnabled: boolean;
};

const definitions: FeatureFlagDefinition[] = [
  {
    key: 'contactForm',
    envName: 'VITE_FEATURE_CONTACT_FORM',
    defaultEnabled: false,
  },
  {
    key: 'caseStudies',
    envName: 'VITE_FEATURE_CASE_STUDIES',
    defaultEnabled: false,
  },
  {
    key: 'availabilityBanner',
    envName: 'VITE_FEATURE_AVAILABILITY_BANNER',
    defaultEnabled: true,
  },
];

const storagePrefix = 'tae.feature.';

function parseFlagValue(value: string | boolean | undefined): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }

  if (value === undefined) {
    return undefined;
  }

  const normalized = value.trim().toLowerCase();
  if (['1', 'true', 'yes', 'on'].includes(normalized)) {
    return true;
  }

  if (['0', 'false', 'no', 'off'].includes(normalized)) {
    return false;
  }

  return undefined;
}

function readLocalOverride(key: FeatureFlagKey): boolean | undefined {
  const override = window.localStorage.getItem(`${storagePrefix}${key}`);
  return override === null ? undefined : parseFlagValue(override);
}

export function isFeatureEnabled(key: FeatureFlagKey): boolean {
  const definition = definitions.find((item) => item.key === key);

  if (!definition) {
    return false;
  }

  const localOverride = readLocalOverride(key);
  if (localOverride !== undefined) {
    return localOverride;
  }

  const envValue = parseFlagValue(import.meta.env[definition.envName]);
  return envValue ?? definition.defaultEnabled;
}

export function getFeatureFlags(): Record<FeatureFlagKey, boolean> {
  return definitions.reduce(
    (flags, definition) => ({
      ...flags,
      [definition.key]: isFeatureEnabled(definition.key),
    }),
    {} as Record<FeatureFlagKey, boolean>,
  );
}

export function setFeatureOverride(key: FeatureFlagKey, enabled: boolean): void {
  window.localStorage.setItem(`${storagePrefix}${key}`, String(enabled));
}

